import { ChangeEvent, useEffect, useState, useRef } from "react";
import { useMaterialUIController } from "context";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Divider,
  Grid,
  IconButton,
  Modal,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import FormField from "layouts/pages/account/components/FormField";
import { IEditCategoria } from "interfaces/eventos";

interface Props {
  open: boolean;
  handleOpen: () => void;
  handleAddCategoria: (cat: IEditCategoria) => void;
  edit: IEditCategoria | null;
  tags: { label: string; value: string }[];
}

type FieldForms = {
  isValid: boolean;
  values: IEditCategoria;
};

const FormCatDefault: FieldForms = {
  isValid: false,
  values: {
    id: 0,
    descricao: "",
    qtdMinima: 1,
    idadeInicial: null,
    idadefinal: null,
    tagId: 1,
    sexoId: 2,
  },
};

export default function ModalCategoria({
  open,
  handleOpen,
  tags,
  edit,
  handleAddCategoria,
}: Props) {
  const descriptionRef = useRef<HTMLInputElement>(null);
  const [controller] = useMaterialUIController();
  const { dataConfig } = controller;
  const [formsCategoria, setFormscategoria] = useState(FormCatDefault);

  useEffect(() => {
    if (edit) {
      setFormscategoria({
        isValid: true,
        values: {
          id: edit.id,
          descricao: edit.descricao,
          qtdMinima: edit.qtdMinima,
          idadeInicial: edit.idadeInicial,
          idadefinal: edit.idadefinal,
          tagId: edit.tagId,
          sexoId: edit.sexoId,
        },
      });
    } else {
      setFormscategoria(FormCatDefault);
    }
  }, [edit]);

  const sexo = dataConfig.tipoSexo.map((item: any) => {
    return { label: item.tipo, value: item.id };
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const dataForm = {
      ...formsCategoria,
      values: {
        ...formsCategoria.values,
        [event.target.name]:
          event.target.type === "number"
            ? Number(event.target.value)
            : event.target.value,
      },
    };
    setFormscategoria(dataForm);
  };

  const submitForms = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormscategoria(FormCatDefault);
    descriptionRef.current.focus();
    handleAddCategoria(formsCategoria.values);
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleOpen}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: { xs: "95%", sm: 400, md: 800 },
            position: "relative",
          }}
        >
          <form onSubmit={submitForms}>
            <IconButton
              sx={{ position: "absolute", top: 20, right: 20 }}
              onClick={handleOpen}
            >
              <CloseIcon />
            </IconButton>
            <MDBox sx={{ p: 2 }}>
              <MDTypography variant="h5">
                {edit ? "Edit Categoria" : "Nova Categoria"}
              </MDTypography>
              <MDTypography variant="button" color="text">
                Cadastre as categorias do seu evento
              </MDTypography>
            </MDBox>

            <Grid container spacing={3} sx={{ py: 2 }}>
              <Grid item xs={12} sm={8}>
                <FormField
                  inputRef={descriptionRef}
                  variant="outlined"
                  name="descricao"
                  type="text"
                  label="Descrição Categoria"
                  value={formsCategoria.values.descricao}
                  onChange={handleChange}
                  required
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormField
                  variant="outlined"
                  name="qtdMinima"
                  type="number"
                  label="Qtd Mínima"
                  value={formsCategoria.values.qtdMinima}
                  onChange={handleChange}
                  required
                  sx={{ background: "white" }}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ py: 2 }}>
              <Grid item xs={12} sm={2}>
                <FormField
                  variant="outlined"
                  name="idadeInicial"
                  type="number"
                  label="Idade Inicial"
                  value={formsCategoria.values.idadeInicial}
                  onChange={handleChange}
                  required
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <FormField
                  variant="outlined"
                  name="idadefinal"
                  type="number"
                  label="Idade Final"
                  value={formsCategoria.values.idadefinal}
                  onChange={handleChange}
                  required
                  sx={{ background: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={tags ? false : true}
                  options={tags}
                  value={
                    (tags &&
                      tags.find(
                        (tag: any) =>
                          Number(tag.value) ===
                          Number(formsCategoria.values.tagId)
                      )) ||
                    null
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(event, newValue) => {
                    setFormscategoria({
                      ...formsCategoria,
                      values: {
                        ...formsCategoria.values,
                        tagId: Number(newValue?.value),
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <FormField
                      variant="outlined"
                      {...params}
                      name="tagId"
                      label="TAG"
                      InputLabelProps={{ shrink: true }}
                      required
                      sx={{ background: "white" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={sexo}
                  value={
                    sexo.find(
                      (sexo: any) =>
                        Number(sexo.value) ===
                        Number(formsCategoria.values.sexoId)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormscategoria({
                      ...formsCategoria,
                      values: {
                        ...formsCategoria.values,
                        sexoId: newValue?.value,
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <FormField
                      variant="outlined"
                      {...params}
                      name="sexoId"
                      label="Sexo"
                      InputLabelProps={{ shrink: true }}
                      required
                      sx={{ background: "white" }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider />
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "end",
                gap: 2,
              }}
            >
              {tags ? (
                <MDButton variant="contained" color="info" type="submit">
                  {edit ? "Salvar alteração" : "Cadastrar"}
                </MDButton>
              ) : (
                <Alert sx={{ width: "100%" }} severity="warning">
                  <AlertTitle>Atenção</AlertTitle>O evento não possui nenhum
                  lote, não sendo possível criar
                </Alert>
              )}

              <MDButton
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleOpen}
              >
                Fechar
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>
    </div>
  );
}

const style = {
  position: "absolute" as "absolute",
  borderRadius: "5px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};
